import { Injectable, computed, effect, inject, untracked } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { ALL_TAGS, PREDEFINED_REPORTS, REPORTS_SEED } from '@common/data';
import { toPersistedReport } from '@common/functions';
import type { PersistedReport, Report, ReportPage, ReportQuery, ReportTag } from '@common/types';

import { UserPreferencesService } from './user-preferences.service';

const LIST_LATENCY_MS = 500;
const DETAIL_LATENCY_MS = 300;
const MAX_FAVOURITE_LINKS = 5;

@Injectable({ providedIn: 'root' })
export class ReportsService {
  private readonly preferences = inject(UserPreferencesService);

  readonly availableTags = computed(() => this.preferences.tags());

  // Global search indexes reports through this service rather than reading
  // preferences directly, so the seeding in the constructor is guaranteed to
  // have run before anyone searches.
  readonly allReports = computed(() => this.preferences.reports());

  readonly predefinedList = computed(() =>
    this.preferences
      .reports()
      .filter((r) => r.type === 'predefined')
      .map((r) => ({ id: r.id, name: r.name, description: r.description })),
  );

  readonly favouriteList = computed(() =>
    this.preferences
      .reports()
      .filter((r) => r.isFavourite)
      .slice(0, MAX_FAVOURITE_LINKS)
      .map((r) => ({ id: r.id, name: r.name, description: r.description })),
  );

  constructor() {
    this.seedPreferences();
    // Preferences belong to whoever is signed in. A second sign-in in the same tab loads a different
    // person's store, which needs the predefined reports and tags too.
    effect(() => {
      this.preferences.owner();
      untracked(() => this.seedPreferences());
    });
  }

  private seedPreferences(): void {
    const storedReports = this.preferences.get('reports');
    if (storedReports.length === 0) {
      this.preferences.set('reports', REPORTS_SEED.map(toPersistedReport));
    } else {
      const existingIds = new Set(storedReports.map((r) => r.id));
      const missing = PREDEFINED_REPORTS.filter((r) => !existingIds.has(r.id));
      if (missing.length > 0) {
        this.preferences.set('reports', [...missing.map(toPersistedReport), ...storedReports]);
      }
    }
    if (this.preferences.get('tags').length === 0) {
      this.preferences.set('tags', [...ALL_TAGS]);
    }
  }

  createTag(tag: Omit<ReportTag, 'id'>): ReportTag {
    const id = tag.name.trim().toLowerCase().replace(/\s+/g, '-');
    const newTag: ReportTag = { ...tag, id };
    this.preferences.set('tags', [...this.preferences.get('tags'), newTag]);
    return newTag;
  }

  updateTag(id: string, patch: Partial<Omit<ReportTag, 'id'>>): void {
    const current = this.preferences.get('tags');
    const idx = current.findIndex((t) => t.id === id);
    if (idx === -1) return;
    const next = [...current];
    next[idx] = { ...next[idx], ...patch };
    this.preferences.set('tags', next);
    const updated = next[idx];
    const reports = this.preferences.get('reports').map((r) => {
      if (!r.tags?.some((t) => t.id === id)) return r;
      return { ...r, tags: r.tags.map((t) => (t.id === id ? updated : t)) };
    });
    this.preferences.set('reports', reports);
  }

  deleteTag(id: string): void {
    this.preferences.set(
      'tags',
      this.preferences.get('tags').filter((t) => t.id !== id),
    );
    const reports = this.preferences
      .get('reports')
      .map((r) =>
        r.tags?.some((t) => t.id === id) ? { ...r, tags: r.tags!.filter((t) => t.id !== id) } : r,
      );
    this.preferences.set('reports', reports);
  }

  private toReport(r: PersistedReport): Report {
    return {
      ...r,
      createdAt: new Date(r.createdAt),
      updatedAt: new Date(r.updatedAt),
    };
  }

  setFavourite(id: string, isFavourite: boolean): void {
    const current = this.preferences.get('reports');
    const idx = current.findIndex((r) => r.id === id);
    if (idx === -1) return;
    const next = [...current];
    next[idx] = { ...next[idx], isFavourite };
    this.preferences.set('reports', next);
  }

  list(query: ReportQuery): Observable<ReportPage> {
    let result: Report[] = this.preferences.get('reports').map((r) => this.toReport(r));

    const term = query.search.trim().toLowerCase();
    if (term.length > 0) {
      result = result.filter(
        (report) =>
          report.name.toLowerCase().includes(term) ||
          report.description.toLowerCase().includes(term) ||
          report.createdBy.toLowerCase().includes(term) ||
          (report.tags ?? []).some((tag) => tag.name.toLowerCase().includes(term)),
      );
    }

    const typeFilters = query.filters['type'];
    if (typeFilters?.length) {
      const allowed = new Set(typeFilters);
      result = result.filter((r) => allowed.has(r.type));
    }

    const formatFilters = query.filters['format'];
    if (formatFilters?.length) {
      const allowed = new Set(formatFilters);
      result = result.filter((r) => allowed.has(r.format));
    }

    const tagFilters = query.filters['tags'];
    if (tagFilters?.length) {
      const allowed = new Set(tagFilters);
      result = result.filter((r) => r.tags?.some((t) => allowed.has(t.id)));
    }

    if (query.sortField) {
      const field = query.sortField as keyof Report;
      const direction = query.sortOrder === 'desc' ? -1 : 1;
      result.sort((a, b) => {
        const av = a[field] ?? '';
        const bv = b[field] ?? '';
        if (av < bv) return -1 * direction;
        if (av > bv) return 1 * direction;
        return 0;
      });
    }

    const total = result.length;
    const start = Math.max(0, query.first);
    const end = start + Math.max(1, query.rows);
    const data = result.slice(start, end);

    return of({ data, total }).pipe(delay(LIST_LATENCY_MS));
  }

  getById(id: string): Observable<Report | undefined> {
    const r = this.preferences.get('reports').find((r) => r.id === id);
    return of(r ? this.toReport(r) : undefined).pipe(delay(DETAIL_LATENCY_MS));
  }

  create(report: Omit<Report, 'id' | 'createdAt' | 'updatedAt' | 'type'>): Observable<Report> {
    const now = new Date();
    const newReport: Report = {
      ...report,
      type: 'custom',
      id: `report-${Date.now()}`,
      createdAt: now,
      updatedAt: now,
    };
    this.preferences.set('reports', [
      toPersistedReport(newReport),
      ...this.preferences.get('reports'),
    ]);
    return of(newReport).pipe(delay(DETAIL_LATENCY_MS));
  }

  update(id: string, patch: Partial<Report>): Observable<Report | undefined> {
    const current = this.preferences.get('reports');
    const idx = current.findIndex((r) => r.id === id);
    if (idx === -1) return of(undefined).pipe(delay(DETAIL_LATENCY_MS));
    const updated = toPersistedReport({
      ...this.toReport(current[idx]),
      ...patch,
      updatedAt: new Date(),
    });
    const next = [...current];
    next[idx] = updated;
    this.preferences.set('reports', next);
    return of(this.toReport(updated)).pipe(delay(DETAIL_LATENCY_MS));
  }

  delete(id: string): Observable<boolean> {
    this.preferences.set(
      'reports',
      this.preferences.get('reports').filter((r) => r.id !== id),
    );
    return of(true).pipe(delay(DETAIL_LATENCY_MS));
  }
}
